import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { useSetChargePointsTariffFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-tariff.form-validation';
import { SetChargePointsTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-tariff.model';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useCpmsBoGetTariffQuery,
  useCpmsBoGetTariffsPaginatedQuery,
} from '@service/cpms-bo-tariff.service';

import { useChargePointContext } from '../../charge-point.context';

const ChargePointTariffUpdateFormStep1TariffSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<SetChargePointsTariffModel, 'tariffId'>;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { tariffId } = useSetChargePointsTariffFormValidation();
  const {
    customer: { id: customerId },
  } = useChargePointContext();

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    data: tariffsPaginatedQueryData,
    isSuccess: tariffsPaginatedQueryIsSuccess,
  } = useCpmsBoGetTariffsPaginatedQuery(
    {
      limit: 100,
      offset: 0,
      name: search === '' ? undefined : `~${search}`,
    },
    {
      skip: !customerId,
    },
  );

  const { data: tariffQueryData, isSuccess: tariffQueryIsSuccess } =
    useCpmsBoGetTariffQuery(field.value, {
      skip: !field.value,
    });

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (tariffQueryIsSuccess) {
      const tariff =
        tariffQueryData.id === field.value ? tariffQueryData : null;

      if (tariff) {
        setSelectedItem({
          key: tariffQueryData.id,
          label: tariffQueryData.name,
        });
        return;
      }
    }

    setSelectedItem(null);
  }, [tariffQueryData, tariffQueryIsSuccess, field.value]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (tariffsPaginatedQueryIsSuccess) {
      setAutocompleteItems(
        tariffsPaginatedQueryData.docs.map(({ id, name }) => ({
          key: id,
          label: name,
        })) ?? [],
      );
    }
  }, [search, tariffsPaginatedQueryData, tariffsPaginatedQueryIsSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.tariff')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.tariff',
      )}
      required={!!tariffId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

export const ChargePointTariffUpdateFormStep1Controller =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { control } = useFormContext<
      SetChargePointsTariffModel,
      'chargePointIds' | 'tariffId'
    >();
    const { tariffId } = useSetChargePointsTariffFormValidation();

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="tariffId"
        rules={tariffId}
        control={control}
        render={ChargePointTariffUpdateFormStep1TariffSelection}
      />
    );
  };
